import { safeLocalStorage } from '@/lib/safeStorage';
import type {
  ConfirmRegistrationPayload,
  CustomerOtpPayload,
  ForgotPasswordPayload,
  ItemEnvelope,
  LoginPayload,
  RefreshTokenPayload,
  RegisterPayload,
  ResetPasswordPayload,
  ResendConfirmationCodePayload,
  SendOtpPayload,
  VerifyCustomerOtpPayload,
  VerifyOtpPayload,
} from '@ruta/shared';
import {getApiUrl, isDevelopment} from '../core';
import {jwtDecode} from 'jwt-decode';
import {storeTokens} from '../auth';
import {CustomerOtpRequestResponse, CustomerOtpVerifyResponse} from '../types';
import {AUTH_EVENTS, STORAGE_KEYS} from '../../constants';

// =====================
// Authentication Endpoints
// =====================

/**
 * Read either the standard `{ data }` item envelope returned by the API
 * Simplification handlers or the legacy flat-error envelope, then throw a
 * useful Error for non-2xx responses. Returns the unwrapped payload.
 */
async function readEnvelope<T>(res: Response, fallbackMessage: string): Promise<T> {
  if (!res.ok) {
    const body = await res.json().catch(() => ({} as any));
    const message =
      body?.error?.message ||
      body?.message ||
      (typeof body?.error === 'string' ? body.error : undefined) ||
      fallbackMessage;
    const err = new Error(message) as Error & { code?: string };
    const code = body?.error?.code || body?.code;
    if (code) err.code = code;
    throw err;
  }
  if (res.status === 204) {
    return {} as T;
  }
  const body = (await res.json()) as ItemEnvelope<T> | T;
  if (body && typeof body === 'object' && 'data' in (body as any)) {
    return (body as ItemEnvelope<T>).data;
  }
  return body as T;
}

export async function registerUser(email: string, password: string, firstName: string, lastName: string, phoneNumber?: string): Promise<RegisterPayload> {
  const res = await fetch(`${getApiUrl()}/auth/register`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email, password, firstName, lastName, phoneNumber }),
  });
  const data = await readEnvelope<RegisterPayload>(res, 'Registration failed');
  safeLocalStorage.removeItem('ruta_sign_in_method');
  return data;
}

export async function loginUser(email: string, password: string): Promise<LoginPayload> {
  const res = await fetch(`${getApiUrl()}/auth/login`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email, password }),
  });
  const data = await readEnvelope<LoginPayload>(res, 'Invalid email or password. Please try again.');
  safeLocalStorage.removeItem('ruta_sign_in_method');
  return data;
}

/** `POST /auth/login { email }`: the emailed sign-in code is on its way. */
export interface EmailSignInCodeSent {
  message: string;
  challenge: 'EMAIL_OTP';
  /** Hand back with the code. Opaque; it says nothing about whether the email has an account. */
  session: string;
}

/**
 * "Email me a code" (fast signup): asks for an 8-digit sign-in code. An email
 * with no account gets the same answer, so this never says who is on Ruta.
 */
export async function requestEmailSignInCode(email: string): Promise<EmailSignInCodeSent> {
  const res = await fetch(`${getApiUrl()}/auth/login`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email }),
  });
  return readEnvelope<EmailSignInCodeSent>(res, "We couldn't send a code. Try again in a moment.");
}

/** Trades the emailed code for the same tokens a password sign-in returns. */
export async function verifyEmailSignInCode(email: string, code: string, session: string): Promise<LoginPayload> {
  const res = await fetch(`${getApiUrl()}/auth/login`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email, code, session }),
  });
  const data = await readEnvelope<LoginPayload>(res, 'That code is wrong or has expired. Send a new one and try again.');
  safeLocalStorage.removeItem('ruta_sign_in_method');
  return data;
}

export async function requestOtp(phoneNumber: string): Promise<SendOtpPayload> {
  const res = await fetch(`${getApiUrl()}/auth/otp/request`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ phoneNumber }),
  });
  return readEnvelope<SendOtpPayload>(res, 'OTP request failed');
}

export async function verifyOtp(phoneNumber: string, otpCode: string): Promise<VerifyOtpPayload> {
  const res = await fetch(`${getApiUrl()}/auth/otp/verify`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ phoneNumber, otpCode }),
  });
  return readEnvelope<VerifyOtpPayload>(res, 'OTP verification failed');
}

export async function forgotPassword(email: string): Promise<ForgotPasswordPayload> {
  const res = await fetch(`${getApiUrl()}/auth/forgot-password`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email }),
  });
  return readEnvelope<ForgotPasswordPayload>(res, 'Password reset failed');
}

export async function resetPassword(email: string, confirmationCode: string, newPassword: string): Promise<ResetPasswordPayload> {
  const res = await fetch(`${getApiUrl()}/auth/reset-password`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email, confirmationCode, newPassword }),
  });
  return readEnvelope<ResetPasswordPayload>(res, 'Failed to reset password');
}

export async function confirmRegistration(email: string, confirmationCode: string, password?: string): Promise<ConfirmRegistrationPayload> {
  const res = await fetch(`${getApiUrl()}/auth/confirm`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email, confirmationCode, password }),
  });
  const data = await readEnvelope<ConfirmRegistrationPayload>(res, 'Confirmation failed');
  // Email signup must not inherit a previous account's Google/passwordless path.
  safeLocalStorage.removeItem('ruta_sign_in_method');

  // If authentication tokens are returned, store them using the correct storage keys
  if (data.authenticationResult) {
    const { AccessToken, IdToken, RefreshToken } = data.authenticationResult;
    if (AccessToken && IdToken && RefreshToken) {
      storeTokens({
        idToken: IdToken,
        accessToken: AccessToken,
        refreshToken: RefreshToken
      });
    }
  }

  return data;
}

export async function registerOrganization(data: {
  organizationName: string;
  contactEmail: string;
  companyAddress?: string;
  shopAddress?: string;
  shopAddressSameAsCompany?: boolean;
  annualRevenue?: string;
  phoneNumber?: string;
  logoFile?: string;
  logoFileName?: string;
  logoFileType?: string;
  invoiceEmail?: string;
  website?: string;
  numberOfCrews?: number;
}, token?: string) {
  const headers: Record<string, string> = { 'Content-Type': 'application/json' };

  // Add authorization header if token is provided
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }

  const res = await fetch(`${getApiUrl()}/organizations`, {
    method: 'POST',
    headers,
    body: JSON.stringify(data),
  });
  if (!res.ok) throw new Error((await res.json()).message || 'Organization registration failed');
  return res.json();
}

export async function resendConfirmationCode(email: string): Promise<ResendConfirmationCodePayload> {
  const res = await fetch(`${getApiUrl()}/auth/confirmation-codes`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email }),
  });
  return readEnvelope<ResendConfirmationCodePayload>(res, 'Failed to resend confirmation code');
}

// Auto-confirm user in development mode
export async function autoConfirmUserInDev(email: string): Promise<boolean> {
  if (!isDevelopment) {
    return false;
  }

  try {
    console.log('[DEV] Attempting to auto-confirm user:', email);

    const res = await fetch(`${getApiUrl()}/auth/auto-confirm`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email }),
    });

    if (res.ok) {
      console.log('[DEV] User auto-confirmed successfully:', email);
      return true;
    } else {
      const error = await res.json();
      console.log('[DEV] Auto-confirm failed:', error.message);
      return false;
    }
  } catch (error) {
    console.log('[DEV] Auto-confirm failed with error:', error);
    return false;
  }
}

// Refresh user tokens to get updated attributes
export async function refreshUserTokens(): Promise<boolean> {
  try {
    const refreshToken = safeLocalStorage.getItem(STORAGE_KEYS.REFRESH_TOKEN);

    if (!refreshToken) {
      console.error('[refreshUserTokens] No refresh token available');
      return false;
    }

    console.log('[refreshUserTokens] Refreshing tokens...');

    // Pass the org this session is already in so Cognito preTokenGen keeps it instead of
    // stamping the first active membership it finds (RUT-3775).
    let orgId: string | undefined;
    const currentIdToken = safeLocalStorage.getItem(STORAGE_KEYS.ID_TOKEN);
    if (currentIdToken) {
      try {
        const payload = jwtDecode<{ 'custom:orgId'?: string }>(currentIdToken);
        orgId = payload['custom:orgId'];
      } catch {
        /* ignore — refresh still works, preTokenGen falls back to the Users-table org */
      }
    }

    const res = await fetch(`${getApiUrl()}/auth/refresh`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ refreshToken, ...(orgId ? { orgId } : {}) }),
    });

    if (!res.ok) {
      console.error('[refreshUserTokens] Token refresh failed:', await res.text());
      return false;
    }

    const data = await readEnvelope<RefreshTokenPayload>(res, 'Token refresh failed');
    const { idToken, accessToken, refreshToken: newRefreshToken } = data;

    if (!idToken || !accessToken) {
      console.error('[refreshUserTokens] Missing tokens in refresh response');
      return false;
    }

    storeTokens({
      idToken,
      accessToken,
      refreshToken: newRefreshToken || refreshToken,
    });

    // Tell the app the token changed. Auth state — including which organization the signed-in
    // person belongs to — is derived from the token in memory and only re-derived on this event.
    // Storing a new token without announcing it leaves the app reading the old one for the rest of
    // the session. That is what broke setup: creating an organization refreshes the token so it
    // finally carries the new org, but nothing re-read it, so the app still believed the owner had
    // no organization and sent them back to setup every time they finished it — recoverable only
    // by reloading the page (RUT-3117).
    window.dispatchEvent(new Event(AUTH_EVENTS.TOKENS_UPDATED));

    console.log('[refreshUserTokens] Tokens refreshed successfully');
    return true;
  } catch (error) {
    console.error('[refreshUserTokens] Token refresh error:', error);
    return false;
  }
}

// =====================
// Customer Authentication Endpoints
// =====================

export async function requestCustomerOtp(contact: string, orgId: string, customerData?: {
  customerFirstName?: string;
  customerLastName?: string;
  customerEmail?: string;
  customerPhone?: string;
  customerAddress?: string;
  companyName?: string;
}): Promise<CustomerOtpRequestResponse> {
  const body: any = { contact, orgId };
  if (customerData) {
    Object.assign(body, customerData);
  }

  const res = await fetch(`${getApiUrl()}/auth/customer/otp/request`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  return readEnvelope<CustomerOtpPayload>(res, 'Customer OTP request failed') as Promise<CustomerOtpRequestResponse>;
}

export async function verifyCustomerOtp(contact: string, otpCode: string): Promise<CustomerOtpVerifyResponse> {
  const res = await fetch(`${getApiUrl()}/auth/customer/otp/verify`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ contact, otpCode }),
  });
  return readEnvelope<VerifyCustomerOtpPayload>(res, 'Customer OTP verification failed') as Promise<CustomerOtpVerifyResponse>;
}
